import React, { useState } from 'react';
import { Modal, ModalSize } from 'packages/radiant-react/modal';
import { InputText } from 'packages/radiant-react/input/input-text';
import { Colors, Typography } from 'packages/radiant-react/typography';
import styles from './styles.module.scss';
import { Select } from 'packages/radiant-react/select';
import type { SelectOption } from 'packages/radiant-react/select/base-select';
import { Vertical } from 'packages/radiant-react/layout';

interface FontOption {
  value: string;
  label: string;
  name: string;
  link: string;
  weight: string;
  style: string;
}

interface AddFontModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFont: (font: FontOption) => void;
}

interface FontFormData {
  name: string;
  link: string;
  weight: string;
  style: string;
}

const fontWeightOptions = [
  { id: '100', label: 'Thin (100)' },
  { id: '200', label: 'Extra Light (200)' },
  { id: '300', label: 'Light (300)' },
  { id: '400', label: 'Regular (400)' },
  { id: '500', label: 'Medium (500)' },
  { id: '600', label: 'Semi Bold (600)' },
  { id: '700', label: 'Bold (700)' },
  { id: '800', label: 'Extra Bold (800)' },
  { id: '900', label: 'Black (900)' },
];

const fontStyleOptions = [
  { id: 'normal', label: 'Normal' },
  { id: 'italic', label: 'Italic' },
  { id: 'oblique', label: 'Oblique' },
];

export const AddFontModal: React.FC<AddFontModalProps> = ({ isOpen, onClose, onAddFont }) => {
  const [formData, setFormData] = useState<FontFormData>({
    name: '',
    link: '',
    weight: '400',
    style: 'normal',
  });

  const [errors, setErrors] = useState<Partial<FontFormData>>({});

  const handleInputChange =
    (field: keyof FontFormData) => (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const handleDropdownChange =
    (field: keyof FontFormData) => (data: SelectOption | SelectOption[]) => {
      const selectedOption = Array.isArray(data) ? data[0] : data;
      if (selectedOption) {
        setFormData((prev) => ({ ...prev, [field]: selectedOption.id }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<FontFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Font name is required';
    }

    if (!formData.link.trim()) {
      newErrors.link = 'Font link is required';
    } else if (!isValidUrl(formData.link)) {
      newErrors.link = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string): boolean => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const newFont: FontOption = {
        value: formData.name,
        label: formData.name,
        name: formData.name,
        link: formData.link,
        weight: formData.weight,
        style: formData.style,
      };

      onAddFont(newFont);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      link: '',
      weight: '400',
      style: 'normal',
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      size={ModalSize.XSMALL}
      title="Add Font"
      onDismiss={handleClose}
      confirmButton={{
        text: 'Add',
        onConfirm: handleSubmit,
      }}
      dismissButton={{
        text: 'Cancel',
        onClick: handleClose,
      }}
    >
      <Vertical className={styles.modalContent}>
        <Typography variant="body-normal" className={styles.descriptionModal}>
          You can use font file URLs of .woff2 format. Make sure the font link is added in the CSP
          settings or the font url is accessible.
        </Typography>

        <Vertical className={styles.formSection}>
          <Vertical className={styles.inputWrapper}>
            <Typography variant="p-small-modal">Font Name</Typography>
            <InputText
              placeholder="e.g. Open Sans"
              value={formData.name}
              onChange={handleInputChange('name')}
              hasError={!!errors.name}
              helperText={errors.name}
            />
          </Vertical>

          <Vertical className={styles.inputWrapper}>
            <div className={styles.fontLinkLabel}>
              <Typography variant="p-small-modal">Font Link</Typography>
              <Typography variant="p-small-modal" className={styles.url}>
                (URL)
              </Typography>
            </div>
            <InputText
              placeholder="e.g. https://fonts.googleapis.com/css2?family=Open+Sans:wght@400"
              value={formData.link}
              onChange={handleInputChange('link')}
              hasError={!!errors.link}
              helperText={errors.link}
            />
          </Vertical>

          <Vertical className={styles.inputWrapper}>
            <Typography variant="p-small-modal">Font Weight</Typography>
            <Select.Dropdown
              onChange={handleDropdownChange('weight')}
              optionsGroups={[{ options: fontWeightOptions }]}
              selectedOptions={[formData.weight]}
            />
          </Vertical>

          <Vertical className={styles.inputWrapper}>
            <Typography variant="p-small-modal">Font Style</Typography>
            <Select.Dropdown
              onChange={handleDropdownChange('style')}
              optionsGroups={[{ options: fontStyleOptions }]}
              selectedOptions={[formData.style]}
            />
          </Vertical>
        </Vertical>
      </Vertical>
    </Modal>
  );
};
